import { useAppInsightsContext } from '@microsoft/applicationinsights-react-js';
import React, { Component, ReactNode } from 'react';
import { NavigateFunction, useLocation, useNavigate } from 'react-router-dom';

interface ErrorBoundaryProps {
  children: ReactNode;
  /**
   * @deprecated Currently not supported as we navigate to a dedicated error page. Can be reintroduced later if needed.
   */
  fallback?: React.ComponentType;
}

// Functional wrapper to provide hooks to the error boundary
export const ErrorBoundary: React.FC<ErrorBoundaryProps> = ({ children, fallback: FallbackComponent }) => {
  const appInsights = useAppInsightsContext();
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <ErrorBoundaryClass fallback={FallbackComponent} appInsights={appInsights} navigate={navigate} location={location}>
      {children}
    </ErrorBoundaryClass>
  );
};

// Note: We have to use a class component for error boundary functionality
// because React's error boundaries do not work with functional components directly, there is no hook for this purpose.
// https://react.dev/reference/react/Component#catching-rendering-errors-with-an-error-boundary
type LocationType = ReturnType<typeof useLocation>;

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  correlationId?: string;
}

class ErrorBoundaryClass extends Component<
  ErrorBoundaryProps & {
    appInsights: ReturnType<typeof useAppInsightsContext>;
    navigate: NavigateFunction;
    location: LocationType;
  },
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { hasError: false };

  componentDidUpdate(prevProps: Readonly<ErrorBoundaryProps & { appInsights: ReturnType<typeof useAppInsightsContext>; navigate: NavigateFunction; location: LocationType }>) {
    if (this.state.hasError && prevProps.location.pathname !== this.props.location.pathname) {
      this.setState({ hasError: false, error: undefined, correlationId: undefined });
    }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    // Generate correlation ID for tracking in AppInsights
    const now = new Date();
    const day = String(now.getDate()).padStart(2, '0');
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const randomPart = Math.random().toString(36).substring(2, 6);
    const correlationId = `NB-${day}-${month}-${randomPart}`;

    return {
      hasError: true,
      error,
      correlationId,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log to AppInsights if available
    if (this.props.appInsights && this.props.appInsights.trackException) {
      this.props.appInsights.trackException({
        exception: error,
        properties: {
          correlationId: this.state.correlationId,
          componentStack: errorInfo.componentStack,
          wasCaughtInErrorBoundary: true,
        },
      });
    }

    // Log to console for development
    console.error('Error Boundary caught an error:', error, errorInfo);

    if (this.props.location.pathname !== '/error') {
      this.props.navigate('/error', { state: { correlationId: this.state.correlationId }, replace: false });
    }
  }

  render() {
    if (this.state.hasError) {
      // We handle navigation to a dedicated error page. If this is rendered, that means the error page itself has thrown an error
      return (
        <>
          <h1>Something went wrong, and we cannot recover the page</h1>
          <p>Please try refreshing the page or contact support if the issue persists.</p>
          <p>If you contact support, please provide the Error-ID below:</p>
          <p>Error-ID: {this.state.correlationId}</p>
        </>
      );
    }

    return this.props.children;
  }
}
